import mixpanel from 'mixpanel-browser';
import config from './environment';

const { env, app_version } = config;

let initialized = false;
export function initMixpanel() {
  if (initialized) return;
  const token = process.env.MIXPANEL_TOKEN || (window.MIXPANEL_TOKEN || null);
  if (!token) {
    console.warn('Mixpanel token missing. Set MIXPANEL_TOKEN env or window.MIXPANEL_TOKEN');
    return;
  }
  mixpanel.init(token, { debug: true });
  initialized = true;
}

let getCurrentUserId = null;

function sanitizeProps(input) {
  if (!input || typeof input !== 'object') return {};
  const forbiddenExact = new Set([
    'name', '$name', 'first_name', '$first_name', 'last_name', '$last_name', 'full_name'
  ]);
  const out = {};
  for (const [key, value] of Object.entries(input)) {
    const lower = String(key).toLowerCase();
    if (lower.includes('email')) continue;
    if (forbiddenExact.has(key)) continue;
    out[key] = value;
  }
  return out;
}

const setUserIdGetter = (getUserIdFn) => {
  getCurrentUserId = getUserIdFn;
};

const Mixpanel = {
  setUserIdGetter,

  identify: (userId) => {
    initMixpanel();
    if (!initialized || !userId) return;
    try { mixpanel.identify(String(userId)); } catch {}
  },

  alias: (userId, previousId) => {
    initMixpanel();
    if (!initialized || !userId) return;
    try { previousId ? mixpanel.alias(String(userId), String(previousId)) : mixpanel.alias(String(userId)); } catch {}
  },

  peopleSet: (props = {}) => {
    initMixpanel();
    if (!initialized) return;
    try { mixpanel.people.set(sanitizeProps(props)); } catch {}
  },

  track: (eventName, props = {}) => {
    initMixpanel();
    if (!initialized) return;

    const enhancedProps = {
      ...sanitizeProps(props),
      user_id: getCurrentUserId ? getCurrentUserId() : null,
      session_id: getSessionId(),
      user_agent: navigator.userAgent,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      app_version: app_version,
      environment: env,
      page_title: document.title,
      page_path: window.location.pathname,
      scroll_depth: getScrollDepth(),
      time_on_page: getTimeOnPage(),
      is_mobile: window.innerWidth < 768,
      connection_type: getConnectionType(),
      timestamp: new Date().toISOString()
    };

    console.log('Tracking event:', eventName, enhancedProps);
    mixpanel.track(eventName, enhancedProps);
  },

  button: (buttonName, context = {}) => {
    const eventName = `${formatEventName(buttonName)} Button Clicked`;
    Mixpanel.track(eventName, {
      button_name: buttonName,
      button_category: context.category || 'general',
      button_location: context.location || 'unknown',
      ...context
    });
  },

  pageView: (pageName, context = {}) => {
    const eventName = `${formatEventName(pageName)} Page Viewed`;
    Mixpanel.track(eventName, {
      page_name: pageName,
      url: window.location.href,
      referrer: document.referrer,
      load_time: performance.now(),
      view_method: context.method || 'direct',
      ...context
    });
  },

  form: (formName, action, context = {}) => {
    const eventName = `${formatEventName(formName)} Form ${formatEventName(action)}`;
    Mixpanel.track(eventName, {
      form_name: formName,
      form_action: action,
      form_step: context.step || null,
      form_completion_time: context.completionTime || null,
      ...context
    });
  },

  feature: (featureName, action = 'used', context = {}) => {
    const eventName = `${formatEventName(featureName)} ${formatEventName(action)}`;
    Mixpanel.track(eventName, {
      feature_name: featureName,
      feature_action: action,
      feature_category: context.category || 'core',
      usage_duration: context.duration || null,
      ...context
    });
  },

  content: (contentType, operation, context = {}) => {
    const eventName = `${formatEventName(contentType)} ${formatEventName(operation)}`;
    Mixpanel.track(eventName, {
      content_type: contentType,
      content_operation: operation,
      content_id: context.id || null,
      content_size: context.size || null,
      content_category: context.category || null,
      ...context
    });
  },

  search: (searchType, context = {}) => {
    const eventName = `${formatEventName(searchType)} Search Performed`;
    Mixpanel.track(eventName, {
      search_type: searchType,
      search_query: context.query || '',
      search_results_count: context.resultsCount || 0,
      search_filters: context.filters || null,
      ...context
    });
  },

  download: (itemType, format = null, context = {}) => {
    const formatPart = format ? ` as ${format.toUpperCase()}` : '';
    const eventName = `${formatEventName(itemType)} Downloaded${formatPart}`;
    Mixpanel.track(eventName, {
      download_type: itemType,
      download_format: format,
      file_size: context.fileSize || null,
      download_method: context.method || 'direct',
      ...context
    });
  },

  auth: (action, method = null, context = {}) => {
    const methodPart = method ? ` via ${formatEventName(method)}` : '';
    const eventName = `User ${formatEventName(action)}${methodPart}`;
    Mixpanel.track(eventName, {
      auth_action: action,
      auth_method: method,
      auth_success: context.success !== false,
      auth_error: context.error || null,
      ...context
    });
  },

  settings: (settingName, action, context = {}) => {
    const eventName = `${formatEventName(settingName)} Setting ${formatEventName(action)}`;
    Mixpanel.track(eventName, {
      setting_name: settingName,
      setting_action: action,
      old_value: context.oldValue || null,
      new_value: context.newValue || null,
      ...context
    });
  },

  modal: (modalName, action, context = {}) => {
    const eventName = `${formatEventName(modalName)} Modal ${formatEventName(action)}`;
    Mixpanel.track(eventName, {
      modal_name: modalName,
      modal_action: action,
      trigger_source: context.triggerSource || 'unknown',
      ...context
    });
  },

  share: (contentType, platform, context = {}) => {
    const eventName = `${formatEventName(contentType)} Shared on ${formatEventName(platform)}`;
    Mixpanel.track(eventName, {
      shared_content_type: contentType,
      share_platform: platform,
      content_id: context.contentId || null,
      share_method: context.method || 'direct',
      ...context
    });
  },

  error: (errorType, context = {}) => {
    const eventName = `${formatEventName(errorType)} Error Occurred`;
    Mixpanel.track(eventName, {
      error_type: errorType,
      error_message: context.message || '',
      error_stack: context.stack || '',
      error_component: context.component || 'unknown',
      error_severity: context.severity || 'error',
      user_action: context.userAction || 'unknown',
      ...context
    });
  },

  performance: (actionName, metrics, context = {}) => {
    const eventName = `${formatEventName(actionName)} Performance Measured`;
    Mixpanel.track(eventName, {
      action_name: actionName,
      load_time: metrics.loadTime || null,
      response_time: metrics.responseTime || null,
      memory_usage: metrics.memoryUsage || null,
      ...metrics,
      ...context
    });
  },

  custom: (eventName, properties = {}) => {
    Mixpanel.track(eventName, properties);
  },

  navigation: (destination, context = {}) => {
    const eventName = `${formatEventName(destination)} Navigated`;
    Mixpanel.track(eventName, {
      destination: destination,
      navigation_type: context.type || 'link_click',
      source_element: context.sourceElement || 'unknown',
      current_url: window.location.href,
      target_url: context.targetUrl || null,
      ...context
    });
  },

  project: (action, context = {}) => {
    Mixpanel.content('project', action, context);
  },

  task: (action, context = {}) => {
    Mixpanel.content('task', action, context);
  },

  engage: (action, element, context = {}) => {
    const eventName = `${formatEventName(element)} ${formatEventName(action)}`;
    Mixpanel.track(eventName, {
      engagement_action: action,
      element_type: element,
      element_position: getElementPosition(element),
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      ...context
    });
  }
};

function formatEventName(str) {
  if (!str) return '';
  return str
    .toString()
    .split(/[\s_-]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function getSessionId() {
  let sessionId = sessionStorage.getItem('session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('session_id', sessionId);
  }
  return sessionId;
}

function getScrollDepth() {
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
  return scrollHeight > 0 ? Math.round((scrollTop / scrollHeight) * 100) : 0;
}

function getTimeOnPage() {
  const startTime = performance.timing.navigationStart;
  return Math.round((Date.now() - startTime) / 1000);
}

function getConnectionType() {
  return navigator.connection ? navigator.connection.effectiveType : 'unknown';
}

function getElementPosition(element) {
  if (typeof element === 'string') return element;
  return 'unknown';
}

export { mixpanel };
window.Mixpanel = Mixpanel;
export default Mixpanel;
